import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Col, CustomInput, Input, InputGroup, InputGroupAddon, InputGroupText, Label, Row } from "reactstrap";
import { ErrorToaster, successToaster } from "components/common/toast";
import { NewAgentTokenAPI, SaveBookingSettingsAPI } from "network/bookings";
import { errText } from "./errText";
import { ConfirmDelete } from "./HoursEditor";
import { fmtMoney, validInt } from "./settingsLogic";

// Riquadri di Settings: Valore medio a coperto, Regole (salvataggio automatico), Altro.

// ---------- Valore medio a coperto ----------
export const CoverCard = ({ s, onSaved }) => {
  const { t } = useTranslation("bookings");
  const [v, setV] = useState(s.cover_value == null ? "" : String(s.cover_value));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await SaveBookingSettingsAPI({ cover_value: v.trim() });
      onSaved(r);
      setV(r.cover_value == null ? "" : String(r.cover_value));
      successToaster(r.cover_value == null ? t("settings.coverRemoved") : t("settings.coverSaved", { value: fmtMoney(r.cover_value) }));
    } catch (e) {
      ErrorToaster(errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <p className="text-muted small">{t("settings.coverHelp")}</p>
      <div className="d-flex align-items-center flex-wrap">
        <InputGroup style={{ maxWidth: 180 }} className="mr-2 mb-2">
          <InputGroupAddon addonType="prepend"><InputGroupText>€</InputGroupText></InputGroupAddon>
          <Input type="number" min="0" step="0.50" inputMode="decimal" value={v} placeholder="0" onChange={(e) => setV(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && save()} />
        </InputGroup>
        <Button color="primary" size="sm" className="mb-2" disabled={busy} onClick={save}>{t("save")}</Button>
      </div>
      <small className="text-muted">{t("settings.coverHint")}</small>
    </>
  );
};

// ---------- Regole: ogni campo si salva appena cambiato ----------
const INTS = [
  ["max_party_size", 1],
  ["min_notice_hours", 0],
  ["cancel_free_hours", 0],
];
const CHANNELS = [["channel_email", "settings.email"], ["channel_sms", "settings.sms"], ["channel_whatsapp", "settings.whatsapp"]];

export const RulesCard = ({ s, onPatch, onSaved }) => {
  const { t } = useTranslation("bookings");
  const [draft, setDraft] = useState({});
  const timers = useRef({});
  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

  const push = async (patch) => {
    onPatch(patch); // subito in pagina, poi la risposta del server
    try {
      onSaved(await SaveBookingSettingsAPI(patch));
      successToaster(t("settings.rulesSaved"));
    } catch (e) {
      ErrorToaster(errText(e));
    }
  };
  const numChange = (k, min, value) => {
    setDraft((d) => ({ ...d, [k]: value }));
    clearTimeout(timers.current[k]);
    timers.current[k] = null;
    if (!validInt(value, min) || Number(value) === Number(s[k])) return;
    timers.current[k] = setTimeout(() => {
      timers.current[k] = null;
      push({ [k]: Number(value) });
    }, 600);
  };
  // uscendo dal campo si salva subito quanto in attesa
  const numBlur = (k) => {
    if (!timers.current[k]) return;
    clearTimeout(timers.current[k]);
    timers.current[k] = null;
    push({ [k]: Number(draft[k]) });
  };
  const slotOptions = [15, 30].includes(Number(s.slot_minutes)) ? [15, 30] : [Number(s.slot_minutes), 15, 30];

  return (
    <>
      <Row>
        <Col md="6" className="mb-3">
          <Label>{t("settings.rule.slot_minutes")}</Label>
          <Input type="select" value={s.slot_minutes} onChange={(e) => push({ slot_minutes: Number(e.target.value) })}>
            {slotOptions.map((m) => <option key={m} value={m} disabled={![15, 30].includes(m)}>{m}</option>)}
          </Input>
          <small className="text-muted">{t("settings.ruleHelp.slot_minutes")}</small>
        </Col>
        {INTS.map(([k, min]) => {
          const value = draft[k] !== undefined ? draft[k] : s[k];
          const bad = draft[k] !== undefined && !validInt(draft[k], min);
          return (
            <Col md="6" className="mb-3" key={k}>
              <Label>{t(`settings.rule.${k}`)}</Label>
              <Input type="number" min={min} step="1" value={value ?? ""} invalid={bad} onChange={(e) => numChange(k, min, e.target.value)} onBlur={() => numBlur(k)} />
              {bad ? <small className="text-danger">{t("settings.intMin", { min })}</small> : <small className="text-muted">{t(`settings.ruleHelp.${k}`)}</small>}
            </Col>
          );
        })}
      </Row>
      <div className="py-2 border-top">
        <CustomInput type="switch" id="rule-auto-confirm" checked={!!s.auto_confirm} onChange={(e) => push({ auto_confirm: e.target.checked })}
          label={<strong>{t("settings.rule.auto_confirm")}</strong>} />
        <small className="text-muted d-block">{t(s.auto_confirm ? "settings.ruleHelp.auto_on" : "settings.ruleHelp.auto_off")}</small>
      </div>
      <div className="py-2 border-top">
        <strong className="d-block mb-1">{t("settings.rule.channels")}</strong>
        <div className="d-flex flex-wrap">
          {CHANNELS.map(([k, label]) => (
            <CustomInput key={k} type="switch" id={`rule-${k}`} className="mr-4" checked={!!s[k]} onChange={(e) => push({ [k]: e.target.checked })} label={t(label)} />
          ))}
        </div>
        <small className="text-muted d-block">{t("settings.ruleHelp.channels")}</small>
        {s.channel_whatsapp && <p className="small mt-2 mb-0 p-2 rounded" style={{ background: "#eef8f0" }}>{t("settings.whatsappHint")}</p>}
      </div>
    </>
  );
};

// ---------- Altro: impostazioni non previste dalle Regole, con Salva normale ----------
const OTHER_NUM = ["duration_minutes", "max_days_ahead", "reminder_hours"];
const OTHER_TEXT = ["phone", "notify_email", "welcome_message"];

export const OtherCard = ({ s, onSaved }) => {
  const { t } = useTranslation("bookings");
  const pick = (x) => Object.fromEntries([...OTHER_NUM, ...OTHER_TEXT, "enabled"].map((k) => [k, x[k] ?? ""]));
  const [f, setF] = useState(pick(s));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const r = await SaveBookingSettingsAPI(f);
      onSaved(r);
      setF(pick(r));
      successToaster(t("settings.saved"));
    } catch (e) {
      ErrorToaster(errText(e));
    } finally {
      setBusy(false);
    }
  };
  const newToken = async () => {
    try {
      const r = await NewAgentTokenAPI();
      onSaved({ ...s, agent_token: r.agent_token });
      successToaster(t("settings.keyGenerated"));
    } catch (e) {
      ErrorToaster(errText(e));
    }
  };
  return (
    <>
      <CustomInput type="switch" id="other-enabled" className="mb-3" checked={!!f.enabled} onChange={(e) => set("enabled", e.target.checked)}
        label={<strong>{t("settings.open")}</strong>} />
      <Row>
        {OTHER_NUM.map((k) => (
          <Col md="4" key={k} className="mb-3">
            <Label>{t(`settings.num.${k}`)}</Label>
            <Input type="number" min="0" value={f[k]} onChange={(e) => set(k, e.target.value)} />
          </Col>
        ))}
        <Col md="4" className="mb-3"><Label>{t("settings.phone")}</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} /></Col>
        <Col md="4" className="mb-3"><Label>{t("settings.notifyEmail")}</Label><Input type="email" value={f.notify_email} onChange={(e) => set("notify_email", e.target.value)} /></Col>
        <Col md="4" className="mb-3"><Label>{t("settings.welcome")}</Label><Input value={f.welcome_message} onChange={(e) => set("welcome_message", e.target.value)} /></Col>
      </Row>
      <Button color="primary" size="sm" disabled={busy} onClick={save}>{t("save")}</Button>

      <hr />
      <h5>{t("settings.agentTitle")}</h5>
      <p className="text-muted small">{t("settings.agentHelp")}</p>
      {s.agent_token ? <Input readOnly value={s.agent_token} onFocus={(e) => e.target.select()} /> : <p className="small">{t("settings.noKey")}</p>}
      <div className="mt-2">
        {s.agent_token ? (
          <ConfirmDelete label={t("settings.regen")} className="px-0" onConfirm={newToken} />
        ) : (
          <Button size="sm" onClick={newToken}>{t("settings.generate")}</Button>
        )}
        {s.agent_token && <small className="text-muted ml-2">{t("settings.regenHint")}</small>}
      </div>

      <hr />
      <p className="small text-muted">{t("settings.styleMoved")}</p>
      <Link to="/style?area=booking" className="btn btn-sm btn-primary">{t("settings.openStyle")}</Link>
    </>
  );
};
